import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import { NAV_LINKS, BRAND } from "@/lib/site";
import { WA } from "@/lib/whatsapp";
import { WhatsAppButton } from "@/components/common/WhatsAppButton";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Only these routes have a full-bleed dark hero behind the navbar.
  const { pathname } = location;
  const hasDarkHero =
    pathname === "/" ||
    /^\/services\/.+/.test(pathname) ||
    /^\/portfolio\/.+/.test(pathname);

  // Solid (charcoal text on warm-white) unless we're at the top of a dark-hero page.
  const solid = scrolled || !hasDarkHero;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid
          ? "bg-lux-warm-white/95 backdrop-blur-[6px] border-b border-border"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="container-lux flex h-16 sm:h-20 items-center justify-between">
        {/* Logo slot — fixed height so a real logo can be swapped in without layout shift */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex h-10 items-center"
          aria-label={`${BRAND.name} — Home`}
        >
          <span
            className={`font-display text-lg sm:text-xl tracking-[0.04em] leading-none transition-colors duration-500 ${
              solid ? "text-lux-charcoal" : "text-white"
            }`}
          >
            DMS
            <span className="text-lux-gold">.</span>
          </span>
          <span
            className={`ml-2 hidden text-[10px] uppercase tracking-[0.28em] xl:inline transition-colors duration-500 ${
              solid ? "text-muted-foreground" : "text-white/80"
            }`}
          >
            Productions &amp; Films
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative text-[11px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
                  solid
                    ? isActive
                      ? "text-lux-charcoal"
                      : "text-muted-foreground hover:text-lux-charcoal"
                    : isActive
                    ? "text-white"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px w-full origin-center scale-x-0 bg-lux-gold transition-transform duration-300 group-hover:scale-x-100 ${
                      isActive ? "scale-x-100" : ""
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <a
            href={BRAND.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#E1306C] hover:text-[#C13584] transform hover:scale-110 hover:shadow-md transition-all duration-300"
          >
            <FaInstagram className="h-6 w-6" />
          </a>
          <WhatsAppButton
              href={WA.consultation()}
              size="sm"
              variant={solid ? "primary" : "outline"}
              className="transform hover:scale-105 hover:shadow-md transition-all duration-300"
              testId="navbar-whatsapp-cta"
              iconColor="#25D366"
            >
              Enquire on WhatsApp
            </WhatsAppButton>
        </div>

        {/* Mobile trigger */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className={`lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                solid ? "text-lux-charcoal" : "text-white"
              }`}
              aria-label="Open menu"
              data-testid="mobile-menu-open-button"
            >
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[86%] max-w-sm border-l border-border bg-lux-ivory p-0"
          >
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <span className="font-display text-lg tracking-[0.04em] text-lux-charcoal">
                DMS<span className="text-lux-gold">.</span>
              </span>
              <SheetClose asChild>
                <button
                  aria-label="Close menu"
                  data-testid="mobile-menu-close-button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-lux-charcoal hover:bg-lux-beige"
                >
                  <X className="h-5 w-5" />
                </button>
              </SheetClose>
            </div>
            <nav className="flex flex-col px-6 py-6" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `border-b border-border/60 py-4 font-display text-2xl tracking-[-0.01em] transition-colors ${
                      isActive ? "text-lux-gold" : "text-lux-charcoal hover:text-lux-gold"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-8">
                <WhatsAppButton
                  href={WA.consultation()}
                  className="w-full transform hover:scale-105 hover:shadow-md transition-transform duration-250 hover:text-[#25D366]"
                  testId="mobile-whatsapp-cta"
                >
                  Enquire on WhatsApp
                </WhatsAppButton>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
